import type {
  ApiResponse,
  CreateItemData,
  ItemData,
  PermitResponse,
  IntentStatusData,
  DownloadTokenRequest,
  DownloadTokenData,
  PresignResponse,
  DownloadUrlResponse,
  TermsType,
  TermsStatus,
  AcceptTermsResponse,
  AuthUser,
  SellerStats,
  SellerSettings,
  SellerItemsResponse,
  GetSellerItemsParams,
  ApiKeyInfo,
  ApiKeyCreated,
  RedeemCouponResponse,
  ActiveCouponResponse,
} from "./types";
import { ErrorCodes } from "./types";

// Next.js will inline NEXT_PUBLIC_* at build time
// In dev, fallback to /api/v1 (handled by Next.js rewrites)
const API_BASE = process.env.NEXT_PUBLIC_BACKEND_URL || "/api/v1";

class ApiError extends Error {
  constructor(
    public code: number,
    message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  // Handle rate limit (429) - response body may not be valid JSON
  if (response.status === 429) {
    return {
      code: ErrorCodes.RATE_LIMIT_EXCEEDED,
      msg: "Too many requests",
      data: null as T,
    };
  }

  const json = await response.json();
  return json as ApiResponse<T>;
}

// Token refresh callback - will be set by AuthProvider
let onTokenRefresh: (() => Promise<boolean>) | null = null;
// Logout callback - will be set by AuthProvider
let onAuthLogout: (() => Promise<void>) | null = null;

export function setTokenRefreshCallback(callback: () => Promise<boolean>) {
  onTokenRefresh = callback;
}

export function setAuthLogoutCallback(callback: () => Promise<void>) {
  onAuthLogout = callback;
}

/**
 * Check if an error code is auth-related
 */
function isAuthError(code: number): boolean {
  return (
    code === ErrorCodes.MISSING_AUTH ||
    code === ErrorCodes.INVALID_AUTH ||
    code === ErrorCodes.AUTH_INVALID_TOKEN ||
    code === ErrorCodes.AUTH_TOKEN_EXPIRED
  );
}

/**
 * Fetch with automatic token refresh on 401
 * Handles authentication errors by refreshing token and retrying once
 * If refresh fails, triggers logout to clear stale auth state
 */
async function fetchWithAuth(url: string, options?: RequestInit): Promise<Response> {
  const optionsWithCredentials: RequestInit = {
    ...options,
    credentials: "include",
  };

  const response = await fetch(url, optionsWithCredentials);

  // If unauthorized and we have a refresh callback, try to refresh and retry
  if (response.status === 401 && onTokenRefresh) {
    // Clone the response to read error body
    const clonedResponse = response.clone();
    const errorBody = await clonedResponse.json().catch(() => ({}));

    // Refresh if it's any auth-related error
    if (isAuthError(errorBody.code)) {
      const refreshed = await onTokenRefresh();
      if (refreshed) {
        // Retry the request
        return fetch(url, optionsWithCredentials);
      }

      // Refresh failed - trigger logout to clear stale auth state
      if (onAuthLogout) {
        await onAuthLogout();
      }
    }
  }

  return response;
}

// 賣家 API

// 取得 Presigned Upload URL
export async function getPresignedUploadUrl(params: {
  sellerAddress: string;
  fileSize: number;
}): Promise<ApiResponse<PresignResponse>> {
  const response = await fetchWithAuth(`${API_BASE}/uploads/presign`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      seller_address: params.sellerAddress,
      content_type: "application/zip",
      file_size: params.fileSize,
    }),
  });

  return handleResponse<PresignResponse>(response);
}

// 上傳檔案到 R2（支援進度回調）
export async function uploadToR2(
  uploadUrl: string,
  file: Blob,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Upload failed due to network error"));
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("Upload aborted"));
    });

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", "application/zip");
    xhr.send(file);
  });
}

// 建立商品（使用 session_id）
export async function createItem(params: {
  sessionId: string;
  title?: string;
  description?: string;
  price: string;
  sellerAddress: string;
  validFrom: Date;
  validUntil: Date;
  turnstileToken?: string;
}): Promise<ApiResponse<CreateItemData>> {
  const response = await fetchWithAuth(`${API_BASE}/items`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      session_id: params.sessionId,
      title: params.title,
      description: params.description,
      price: params.price,
      seller_address: params.sellerAddress,
      valid_from: params.validFrom.toISOString(),
      valid_until: params.validUntil.toISOString(),
      cf_turnstile_token: params.turnstileToken,
    }),
  });

  return handleResponse<CreateItemData>(response);
}

// 買家 API

export async function getItem(
  itemId: string,
  walletAddress?: string
): Promise<ApiResponse<ItemData>> {
  const params = new URLSearchParams();
  if (walletAddress) {
    params.append("wallet_address", walletAddress);
  }

  const queryString = params.toString();
  const url = `${API_BASE}/items/${itemId}${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url);
  return handleResponse<ItemData>(response);
}

export async function getPermit(
  itemId: string,
  payerAddress: string
): Promise<ApiResponse<PermitResponse>> {
  const params = new URLSearchParams({ payer_address: payerAddress });
  const response = await fetch(`${API_BASE}/items/${itemId}/permit?${params}`);
  return handleResponse<PermitResponse>(response);
}

/**
 * 查詢 Intent 狀態（CDP Webhook 流程）
 * @param intentId - 從 getPermit() 回傳的 intent_id
 */
export async function getIntentStatus(
  intentId: string
): Promise<ApiResponse<IntentStatusData>> {
  const response = await fetch(`${API_BASE}/intents/${intentId}/status`);
  return handleResponse<IntentStatusData>(response);
}

export async function getDownloadToken(
  itemId: string,
  data: DownloadTokenRequest
): Promise<ApiResponse<DownloadTokenData>> {
  const response = await fetch(`${API_BASE}/items/${itemId}/download-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  return handleResponse<DownloadTokenData>(response);
}

// 建立下載訊息（用於錢包簽名）
export function buildDownloadMessage(itemId: string, timestamp: number): string {
  return `I want to download item ${itemId} at ${timestamp}`;
}

// 取得 Presigned Download URL
export async function fetchDownloadUrl(
  token: string
): Promise<ApiResponse<DownloadUrlResponse>> {
  const response = await fetch(`${API_BASE}/download/${token}`);
  return handleResponse<DownloadUrlResponse>(response);
}

// Terms API
export async function checkTermsStatus(
  walletAddress?: string
): Promise<ApiResponse<TermsStatus>> {
  const params = walletAddress
    ? new URLSearchParams({ wallet_address: walletAddress })
    : new URLSearchParams();
  const response = await fetch(`${API_BASE}/terms/check?${params}`, {
    credentials: "include",
  });
  return handleResponse<TermsStatus>(response);
}

export async function acceptTerms(
  walletAddress: string,
  termsType: TermsType
): Promise<ApiResponse<AcceptTermsResponse>> {
  const response = await fetch(`${API_BASE}/terms/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ wallet_address: walletAddress, terms_type: termsType }),
  });
  return handleResponse<AcceptTermsResponse>(response);
}

// Auth API

/**
 * 用 Google ID Token 登入，後端會設置 httpOnly cookies
 */
export async function googleAuth(
  idToken: string
): Promise<ApiResponse<{ user: AuthUser }>> {
  const response = await fetch(`${API_BASE}/auth/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ id_token: idToken }),
  });
  return handleResponse<{ user: AuthUser }>(response);
}

/**
 * 刷新 access_token（從 httpOnly cookie 讀取 refresh_token）
 */
export async function refreshAuthToken(): Promise<ApiResponse<null>> {
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });
  return handleResponse<null>(response);
}

/**
 * 登出（清除 httpOnly cookies）
 */
export async function logout(): Promise<ApiResponse<null>> {
  const response = await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });
  return handleResponse<null>(response);
}

// Seller Dashboard API

/**
 * 取得賣家所有商品（支援分頁）
 */
export async function getSellerItems(
  params?: GetSellerItemsParams
): Promise<ApiResponse<SellerItemsResponse>> {
  const searchParams = new URLSearchParams();
  if (params?.page !== undefined) {
    searchParams.set("page", String(params.page));
  }
  if (params?.limit !== undefined) {
    searchParams.set("limit", String(params.limit));
  }
  if (params?.status && params.status !== "all") {
    searchParams.set("status", params.status);
  }

  const queryString = searchParams.toString();
  const url = `${API_BASE}/seller/items${queryString ? `?${queryString}` : ""}`;
  const response = await fetchWithAuth(url);
  return handleResponse<SellerItemsResponse>(response);
}

/**
 * 取得賣家統計資料
 */
export async function getSellerStats(): Promise<ApiResponse<SellerStats>> {
  const response = await fetchWithAuth(`${API_BASE}/seller/stats`);
  return handleResponse<SellerStats>(response);
}

/**
 * 取得賣家設定
 */
export async function getSellerSettings(): Promise<ApiResponse<SellerSettings>> {
  const response = await fetchWithAuth(`${API_BASE}/seller/settings`);
  return handleResponse<SellerSettings>(response);
}

/**
 * 更新賣家設定
 */
export async function updateSellerSettings(
  settings: Partial<SellerSettings>
): Promise<ApiResponse<SellerSettings>> {
  const response = await fetchWithAuth(`${API_BASE}/seller/settings`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(settings),
  });
  return handleResponse<SellerSettings>(response);
}

// API Key Management

/**
 * 查詢我的 API Key
 */
export async function getApiKey(): Promise<ApiResponse<ApiKeyInfo>> {
  const response = await fetchWithAuth(`${API_BASE}/api-keys`);
  return handleResponse<ApiKeyInfo>(response);
}

/**
 * 建立 API Key
 */
export async function createApiKey(name: string): Promise<ApiResponse<ApiKeyCreated>> {
  const response = await fetchWithAuth(`${API_BASE}/api-keys`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  return handleResponse<ApiKeyCreated>(response);
}

/**
 * 撤銷 API Key
 */
export async function revokeApiKey(): Promise<ApiResponse<null>> {
  const response = await fetchWithAuth(`${API_BASE}/api-keys`, {
    method: "DELETE",
  });
  return handleResponse<null>(response);
}

// Coupon API

/**
 * 兌換優惠券
 */
export async function redeemCoupon(
  couponCode: string
): Promise<ApiResponse<RedeemCouponResponse>> {
  const response = await fetchWithAuth(`${API_BASE}/coupons/redeem`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ coupon_code: couponCode }),
  });
  return handleResponse<RedeemCouponResponse>(response);
}

/**
 * 查詢生效中的優惠券及目前手續費
 */
export async function getActiveCoupon(): Promise<ApiResponse<ActiveCouponResponse>> {
  const response = await fetchWithAuth(`${API_BASE}/coupons/active`);
  return handleResponse<ActiveCouponResponse>(response);
}

export { ApiError };
